import React, { useCallback, useMemo, useRef, useState } from "react";

import { CalendarApi } from "@fullcalendar/common";
import cn from "classnames";

import { AnyEntity, EntityType } from "@mapmycustomers/shared/types/entity";
import IFieldModel from "@mapmycustomers/shared/types/fieldModel/IFieldModel";
import User from "@mapmycustomers/shared/types/User";
import FilterModel from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";

import ISchedulerViewContext from "@app/component/view/SchedulerView/types/ISchedulerViewContext";
import CalendarViewMode from "@app/enum/CalendarViewMode";
import Fetcher from "@app/types/viewModel/Fetcher";

import { ViewStateUpdater } from "../types/IViewContext";
import ViewDataFetcher from "../types/ViewDataFetcher";
import mergeFilterModels from "../utils/mergeFilterModels";
import ViewContext from "../utils/ViewContext";

import styles from "./SchedulerView.module.scss";
import SchedulerViewComponent from "./types/SchedulerViewComponent";
import SchedulerViewState from "./types/SchedulerViewState";

interface Props<T extends AnyEntity> {
  children: React.ReactNode;
  className?: string;
  entityType?: EntityType;
  fetcher: Fetcher<T, ListResponse<T>, SchedulerViewState>;
  fieldModel: IFieldModel;
  totalRecords?: number;
  viewState: SchedulerViewState;
}

const SchedulerView = <T extends AnyEntity>({
  children,
  className,
  entityType,
  fetcher,
  fieldModel,
  totalRecords,
  viewState,
}: Props<T>) => {
  const [calendarApi, setCalendarApi] = useState<CalendarApi | undefined>();
  const components = useRef<Set<SchedulerViewComponent<T>>>(new Set());

  const handleUpdateViewState: ViewStateUpdater<SchedulerViewState> = useCallback(
    (viewState: Partial<SchedulerViewState>) => {
      fetcher({ updateOnly: true, viewState });
      Array.from(components.current).forEach((callback) => {
        callback.onViewStateChange?.(viewState);
      });
    },
    [fetcher]
  );

  const handleUpdateViewStateAndFetch: ViewDataFetcher<
    ListResponse<T>,
    SchedulerViewState
  > = useCallback(
    (
      viewState: Partial<SchedulerViewState>,
      dataCallback?: (response: ListResponse<T>) => void,
      failCallback?: () => void
    ) => fetcher({ dataCallback, failCallback, updateOnly: false, viewState }),
    [fetcher]
  );

  const handleUpdateFilters: ISchedulerViewContext["updateFilterModel"] = useDynamicCallback(
    (newFilterModel: Partial<FilterModel>) => {
      const resultingFilterModel = mergeFilterModels(viewState.filter, newFilterModel);
      handleUpdateViewState({ filter: resultingFilterModel });
      Array.from(components.current).forEach((callback) => {
        callback.onFilterChange?.(newFilterModel);
      });
    }
  );

  const handleColumnVisibilityChange: ISchedulerViewContext["updateColumnVisibility"] =
    useCallback(() => {
      // Doing nothing because calendar views have no columns
    }, []);

  const handleUpdateEntity: ISchedulerViewContext["updateEntity"] = useDynamicCallback(
    (entity: T) => {
      Array.from(components.current).forEach((callback) => {
        callback.onUpdateEntity?.(entity);
      });
    }
  );

  const handleUpdateViewAs: ISchedulerViewContext["updateViewAs"] = useDynamicCallback(
    (userId: undefined | User["id"]) => {
      handleUpdateViewState({ viewAs: userId });
      Array.from(components.current).forEach((callback) => {
        callback.onViewAsChange?.(userId);
      });
    }
  );

  const handleViewModeChange = useDynamicCallback((viewMode: CalendarViewMode) =>
    handleUpdateViewState({ viewMode })
  );

  const handleRegister = useCallback(
    (component: SchedulerViewComponent<T>) => {
      components.current.add(component);
    },
    [components]
  );

  const handleUnregister = useCallback(
    (component: SchedulerViewComponent<T>) => {
      components.current.delete(component);
    },
    [components]
  );

  const context: ISchedulerViewContext<T> = useMemo(
    () => ({
      calendarApi,
      changeViewMode: handleViewModeChange,
      entityType,
      fieldModel,
      register: handleRegister,
      setCalendarApi,
      totalRecords,
      unregister: handleUnregister,
      updateColumnVisibility: handleColumnVisibilityChange,
      updateEntity: handleUpdateEntity,
      updateFilterModel: handleUpdateFilters,
      updateViewAs: handleUpdateViewAs,
      updateViewState: handleUpdateViewState,
      updateViewStateAndFetch: handleUpdateViewStateAndFetch,
      viewState,
    }),
    [
      calendarApi,
      entityType,
      fieldModel,
      handleUpdateFilters,
      handleColumnVisibilityChange,
      handleUpdateEntity,
      handleUpdateViewAs,
      handleRegister,
      handleUnregister,
      handleUpdateViewState,
      handleUpdateViewStateAndFetch,
      handleViewModeChange,
      viewState,
      totalRecords,
    ]
  );

  return (
    <section className={cn(styles.container, className, "activity-scheduler-view")}>
      <ViewContext.Provider
        // Ignored because TS can't be sure that the Provider was created for exactly same
        // entity type as T. Although we know that it will be.
        // @ts-ignore
        value={context}
      >
        {children}
      </ViewContext.Provider>
    </section>
  );
};

export default SchedulerView;
